import React, { useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  DialogContent,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PersonOutline as PersonOutlineIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  useGetMyInvitesQuery,
  useRespondToInviteMutation,
  GetProjectsDocument,
} from '../../../generated/graphql';
import { useNotification } from '../notification/NotificationContext';
import { formatDate } from '../../utils/fileUtils';
import DialogWindow from './DialogWindow';

const InvitationNotifications: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, showError } = useNotification();

  const {
    data: invitesData,
    loading: invitesLoading,
    refetch: refetchInvites,
  } = useGetMyInvitesQuery({
    // Poll every 60 seconds
    pollInterval: 60000,
  });

  const [respondToInvite, { loading: respondLoading }] =
    useRespondToInviteMutation({
      refetchQueries: [
        { query: GetProjectsDocument }, // This will refetch the projects list
      ],
    });

  const pendingInvites = invitesData?.projectInvites || [];

  const handleRespondToInvite = async (inviteId: string, accept: boolean) => {
    try {
      await respondToInvite({
        variables: {
          data: {
            inviteId,
            accept,
          },
        },
      });

      showSuccess(
        accept
          ? 'Invitation accepted! You now have access to the project.'
          : 'Invitation declined.'
      );

      refetchInvites();
    } catch (error) {
      console.error('Error responding to invitation:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to respond to invitation';
      showError(errorMessage);
    }
  };

  if (invitesLoading) {
    return (
      <IconButton disabled>
        <CircularProgress size={20} />
      </IconButton>
    );
  }

  return (
    <>
      <Tooltip title="Project invitations">
        <IconButton
          onClick={() => setDialogOpen(true)}
          aria-label="Project invitations"
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'studio.borders.control',
            bgcolor: 'studio.surfaces.fillWeak',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.45),
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09),
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Badge badgeContent={pendingInvites.length} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <DialogWindow
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Project Invitations"
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          {pendingInvites.length === 0 ? (
            <Alert severity="info">
              You have no pending project invitations.
            </Alert>
          ) : (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  px: { xs: 0.5, sm: 0 },
                  py: 0.8,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  bgcolor: 'studio.surfaces.fill',
                  border: (theme) =>
                    `1px solid ${theme.palette.studio.borders.subtle}`,
                  textAlign: 'center',
                }}
              >
                You have {pendingInvites.length} pending invitation
                {pendingInvites.length !== 1 ? 's' : ''}
              </Typography>

              <List sx={{ py: 0 }}>
                {pendingInvites.map((invite) => (
                  <Paper
                    key={invite._id}
                    sx={{
                      mb: 1.5,
                      borderRadius: 2.5,
                      border: (theme) =>
                        `1px solid ${theme.palette.studio.borders.control}`,
                      bgcolor: 'studio.surfaces.fill',
                      backgroundImage: (theme) =>
                        theme.palette.studio.surfaces.cardGradientSoft,
                      boxShadow: (theme) => theme.palette.studio.shadows.card,
                    }}
                  >
                    <ListItem
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 2,
                        p: { xs: 1.75, sm: 2.25 },
                      }}
                    >
                      <ListItemText
                        sx={{ m: 0 }}
                        primary={
                          <Box
                            display="flex"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            gap={1.1}
                            mb={1.2}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: { xs: '1.35rem', sm: '1.2rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                              }}
                            >
                              {invite.projectName}
                            </Typography>
                            <Chip
                              label={invite.role}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                color: 'white',
                                bgcolor: 'secondary.main',
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'grid', gap: 0.8 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                color: 'text.secondary',
                              }}
                            >
                              <PersonOutlineIcon
                                sx={{ fontSize: 18, opacity: 0.85 }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Invited by: {invite?.invitedByEmail}
                              </Typography>
                            </Box>
                            {/* Removed recipient email row to reduce noise and avoid showing current user's email. */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                color: 'text.secondary',
                              }}
                            >
                              <ScheduleIcon
                                sx={{ fontSize: 18, opacity: 0.85 }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: 'block',
                                  mt: 0.1,
                                  fontSize: '0.95rem',
                                }}
                              >
                                Expires: {formatDate(invite.expiresAt)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.25,
                          flexDirection: { xs: 'column', sm: 'row' },
                          width: { xs: '100%', sm: 'auto' },
                          justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        }}
                      >
                        <Button
                          variant="contained"
                          color="success"
                          size="large"
                          startIcon={<CheckIcon />}
                          onClick={() =>
                            handleRespondToInvite(invite._id, true)
                          }
                          disabled={respondLoading}
                          fullWidth
                          sx={{
                            borderRadius: 1.5,
                            fontWeight: 700,
                            py: { xs: 1.1, sm: 0.9 },
                            boxShadow: 'none',
                          }}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="large"
                          startIcon={<CloseIcon />}
                          onClick={() =>
                            handleRespondToInvite(invite._id, false)
                          }
                          disabled={respondLoading}
                          fullWidth
                          sx={{
                            borderRadius: 1.5,
                            fontWeight: 700,
                            py: { xs: 1.1, sm: 0.9 },
                            borderColor: 'error.main',
                          }}
                        >
                          Decline
                        </Button>
                      </Box>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </DialogWindow>
    </>
  );
};

export default InvitationNotifications;
